// Contrail V3.7.0 — mobile map: live markers, zones (view only), map type, "Fleet right now" filter chip,
// My position (blue dot, display only — never sent anywhere) and follow-an-object.
import { useEffect, useRef, useState } from "react";
import { Map as MLMap, Marker, LngLatBounds, setWorkerUrl, type GeoJSONSource } from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import type { Device } from "../lib/supabase";
import { liveSpeed, statusOf, STATUS_LABEL, type Status } from "../lib/format";
import { MK_OFFSET, markerParts, setSilhouette } from "../lib/vehicleMarker";
import { ALL_BASEMAPS, MAP_STYLE, applyBasemap, saveBasemap, savedBasemap, type Basemap } from "../lib/basemap";
import BasemapPicker from "../ui/BasemapPicker";
import { circle, zoneFeature } from "../lib/geo";
import { onStyleReady } from "../lib/mapready";
import type { Zone } from "../lib/org";
setWorkerUrl(maplibreWorkerUrl);

export type MyPos = { lat: number; lng: number; acc: number };
type LocMode = "off" | "on" | "follow";
const FIJI: [number, number] = [177.45, -17.75];

export default function MobileMap({ devices, now, zones, basemaps, selectedId, followId, filter, onSelect, onFollowEnd, onMyPos, onMap, onToast, onFilter }: {
  devices: Device[]; now: number; zones: Zone[]; basemaps: Basemap[] | null;
  selectedId: string | null; followId: string | null; filter: Status | null;
  onSelect: (id: string | null) => void; onFollowEnd: () => void; onMyPos: (p: MyPos | null) => void;
  onMap: (m: MLMap | null) => void; onToast: (msg: string) => void; onFilter: (s: Status | null) => void;
}) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MLMap | null>(null);
  const [mapObj, setMapObj] = useState<MLMap | null>(null);
  const markers = useRef(new Map<string, Marker>());
  const fitted = useRef(false);
  const [basemap, setBasemap] = useState<Basemap>(savedBasemap(ALL_BASEMAPS));
  useEffect(() => { if (basemaps && !basemaps.includes(basemap)) setBasemap("streets"); }, [basemaps]); // eslint-disable-line

  // ---- map
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = new MLMap({ container: el.current, style: MAP_STYLE, center: FIJI, zoom: 8, attributionControl: false, pitchWithRotate: false, dragRotate: false });
    m.touchZoomRotate.disableRotation();
    map.current = m; setMapObj(m); onMap(m);
    const ro = new ResizeObserver(() => m.resize()); ro.observe(el.current);
    return () => { ro.disconnect(); m.remove(); map.current = null; setMapObj(null); onMap(null); markers.current.clear(); };
  }, []); // eslint-disable-line
  useEffect(() => { if (mapObj) { saveBasemap(basemap); applyBasemap(mapObj, basemap); } }, [basemap, mapObj]);

  // ---- markers
  useEffect(() => {
    const m = map.current; if (!m) return;
    const seen = new Set<string>(); const pts: [number, number][] = [];
    for (const d of devices) {
      const s = d.state; if (!s || s.lat == null || s.lng == null) continue;
      const st = statusOf(s, now);
      if (filter && st !== filter && !(filter === "offline" && st === "nodata")) continue;
      seen.add(d.id); pts.push([s.lng, s.lat]);
      let mk = markers.current.get(d.id);
      if (!mk) {
        const b = document.createElement("button");
        b.className = "mk m-mk"; b.type = "button";
        b.addEventListener("click", (e) => { e.stopPropagation(); onSelect(d.id); });
        mk = new Marker({ element: b, anchor: "left", offset: MK_OFFSET }).setLngLat([s.lng, s.lat]).addTo(m);
        markers.current.set(d.id, mk);
      }
      mk.setLngLat([s.lng, s.lat]);
      const e = mk.getElement();
      e.dataset.status = st; e.dataset.selected = String(selectedId === d.id); e.dataset.quality = s.quality ?? "good";
      e.dataset.custom = d.icon_color ? "true" : "false";
      if (d.icon_color) e.style.setProperty("--mk-bg", d.icon_color); else e.style.removeProperty("--mk-bg");
      e.setAttribute("aria-label", `${d.name}, ${STATUS_LABEL[st]}, ${liveSpeed(s, st)}`);
      const { dot, label } = markerParts(e);
      setSilhouette(dot, d.icon, s.course, s.speed_kn == null ? null : Number(s.speed_kn) * 1.852);
      label!.textContent = d.name;
    }
    for (const [id, mk] of markers.current) if (!seen.has(id)) { mk.remove(); markers.current.delete(id); }
    if (!fitted.current && pts.length) {
      fitted.current = true;
      if (pts.length === 1) m.jumpTo({ center: pts[0], zoom: 14 });
      else { const b = new LngLatBounds(pts[0], pts[0]); pts.forEach((p) => b.extend(p)); m.fitBounds(b, { padding: { top: 90, bottom: 60, left: 40, right: 40 }, maxZoom: 14, duration: 0 }); }
    }
  }, [devices, now, selectedId, filter]); // eslint-disable-line

  // ---- follow an object
  const follow = useRef(followId); follow.current = followId;
  useEffect(() => {
    const m = map.current; if (!m || !followId) return;
    const s = devices.find((d) => d.id === followId)?.state;
    if (s?.lat != null && s.lng != null) m.easeTo({ center: [s.lng, s.lat], zoom: Math.max(m.getZoom(), 14), duration: 600 });
  }, [followId, devices]); // eslint-disable-line
  useEffect(() => {
    const m = mapObj; if (!m) return;
    const stop = (e: any) => { if (!e.originalEvent) return; if (follow.current) onFollowEnd(); if (loc_.current === "follow") setLoc("on"); };
    m.on("dragstart", stop);
    return () => { m.off("dragstart", stop); };
  }, [mapObj]); // eslint-disable-line

  // ---- zones (view only)
  useEffect(() => {
    const m = mapObj; if (!m) return;
    onStyleReady(m, () => {
      const data = { type: "FeatureCollection" as const, features: zones.map((z) => ({ ...zoneFeature(z), properties: { color: z.color, name: z.name } })) };
      const src = m.getSource("m-zones") as GeoJSONSource | undefined;
      if (src) { src.setData(data); return; }
      m.addSource("m-zones", { type: "geojson", data });
      m.addLayer({ id: "m-zones-fill", type: "fill", source: "m-zones", paint: { "fill-color": ["get", "color"], "fill-opacity": 0.08 } });
      m.addLayer({ id: "m-zones-line", type: "line", source: "m-zones", paint: { "line-color": ["get", "color"], "line-width": 1.5, "line-dasharray": [2, 1] } });
    }, "m-zones");
  }, [zones, mapObj]);

  // ---- My position (asks for permission only on the first tap; display only)
  const [loc, setLocState] = useState<LocMode>("off");
  const loc_ = useRef<LocMode>("off");
  const setLoc = (v: LocMode) => { loc_.current = v; setLocState(v); };
  const watchId = useRef<number | null>(null);
  const meMarker = useRef<Marker | null>(null);
  const firstFix = useRef(false);
  const stopLocating = () => {
    if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
    watchId.current = null; meMarker.current?.remove(); meMarker.current = null; firstFix.current = false;
    const m = map.current; const src = m?.getSource("m-me-acc") as GeoJSONSource | undefined;
    src?.setData({ type: "FeatureCollection", features: [] });
    onMyPos(null);
  };
  useEffect(() => () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); }, []);
  function onFix(p: GeolocationPosition) {
    const m = map.current; if (!m) return;
    const me: MyPos = { lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy };
    onMyPos(me);
    if (!meMarker.current) {
      const dot = document.createElement("div"); dot.className = "m-me"; dot.setAttribute("aria-label", "My position");
      meMarker.current = new Marker({ element: dot }).setLngLat([me.lng, me.lat]).addTo(m);
    } else meMarker.current.setLngLat([me.lng, me.lat]);
    onStyleReady(m, () => {
      const data = { type: "FeatureCollection" as const, features: [circle([me.lng, me.lat], Math.max(5, me.acc))] };
      const src = m.getSource("m-me-acc") as GeoJSONSource | undefined;
      if (src) src.setData(data);
      else {
        m.addSource("m-me-acc", { type: "geojson", data });
        m.addLayer({ id: "m-me-acc", type: "fill", source: "m-me-acc", paint: { "fill-color": "#1A73E8", "fill-opacity": 0.12 } });
      }
    }, "m-me");
    if (!firstFix.current || loc_.current === "follow") {
      firstFix.current = true;
      m.easeTo({ center: [me.lng, me.lat], zoom: Math.max(m.getZoom(), 15), duration: 700 });
    }
  }
  function onErr(e: GeolocationPositionError) {
    stopLocating(); setLoc("off");
    onToast(e.code === e.PERMISSION_DENIED ? "Location is off. Allow it in your phone settings to see your position."
      : "Couldn't find your position right now. Try again outdoors or with location switched on.");
  }
  function locate() {
    if (!("geolocation" in navigator)) { onToast("This phone or browser can't share its location."); return; }
    if (loc_.current === "off") {
      setLoc("on");
      watchId.current = navigator.geolocation.watchPosition(onFix, onErr, { enableHighAccuracy: true, maximumAge: 10_000, timeout: 20_000 });
    } else if (loc_.current === "on") {
      setLoc("follow"); firstFix.current = false;
      if (meMarker.current) map.current?.easeTo({ center: meMarker.current.getLngLat(), zoom: Math.max(map.current.getZoom(), 15) });
    } else { stopLocating(); setLoc("off"); }
  }

  // ---- fleet chip
  const counts = { moving: 0, stopped: 0, late: 0, offline: 0 };
  for (const d of devices) { const st = statusOf(d.state, now); if (st === "moving") counts.moving++; else if (st === "stopped") counts.stopped++; else if (st === "late") counts.late++; else if (d.state) counts.offline++; }
  const chip = (k: "moving" | "stopped" | "late" | "offline", label: string) => (
    <button type="button" className={`m-fc ${k} ${filter === k ? "on" : ""}`} aria-pressed={filter === k} onClick={() => onFilter(filter === k ? null : k)}>
      <i className={`dot ${k}`} /><b>{counts[k]}</b><span>{label}</span>
    </button>
  );

  return (
    <div className="m-mapwrap">
      <div className="m-map" ref={el} onClick={() => onSelect(null)} />
      <div className="m-maptop">
        <div className="m-fleetchip" role="group" aria-label="Fleet right now — tap to filter">
          {chip("moving", "Moving")}{chip("stopped", "Stopped")}{(counts.late > 0 || filter === "late") && chip("late", "No signal")}{chip("offline", "Offline")}
        </div>
      </div>
      <div className="m-mapbtns">
        <BasemapPicker value={basemap} options={basemaps} onChange={setBasemap} />
        <button type="button" className={`m-locate ${loc}`} onClick={locate}
          aria-label={loc === "off" ? "Show my position" : loc === "on" ? "Follow my position" : "Stop showing my position"}
          title={loc === "off" ? "Show my position" : loc === "on" ? "Follow me" : "Turn off my position"}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            {loc === "follow" ? <path d="M12 2 4.5 20.3l.7.7L12 18l6.8 3 .7-.7z" fill="currentColor" /> : <><circle cx="12" cy="12" r="4" fill={loc === "on" ? "currentColor" : "none"} /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>}
          </svg>
        </button>
      </div>
    </div>
  );
}
